<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="dialogVisible"
    width="1200px"
    top="5vh"
    append-to-body
    custom-class="hpc-dialog"
    :close-on-click-modal="false"
    :close-on-press-escape="!busy"
    :show-close="!busy"
    :before-close="handleBeforeClose"
    @close="handleClose">
    <div class="hpc-layout" :class="{ 'hpc-layout--teacher': personType === 'teacher' }">
      <div v-if="personType === 'student'" class="hpc-left">
        <DepartmentPane directory-scope="teaching"
          :type="0"
          @tree-node-click="onTreeNodeClick">
        </DepartmentPane>
      </div>
      <div class="hpc-right">
        <el-tabs
          v-model="activeMode"
          class="hpc-tabs"
          :before-leave="beforeTabLeave">
          <el-tab-pane label="按大小压缩" name="compress"></el-tab-pane>
          <el-tab-pane label="转 JPG" name="convertJpg"></el-tab-pane>
        </el-tabs>

        <div class="toolbar-filter-bar toolbar-filter-bar--embed">
          <el-form :inline="true" class="toolbar-filter-form" @submit.native.prevent="runScan">
            <div class="toolbar-filter-fields">
              <el-form-item>
                <el-input
                  v-model="nameKeyword"
                  clearable
                  :placeholder="personType === 'student' ? '姓名' : '教师姓名'"
                  class="toolbar-field-w-md"
                  :disabled="busy"
                  @keyup.enter.native="runScan">
                </el-input>
              </el-form-item>
              <el-form-item v-if="personType === 'student'">
                <el-input
                  v-model="cardNoKeyword"
                  clearable
                  placeholder="卡号"
                  class="toolbar-field-w-md"
                  :disabled="busy"
                  @keyup.enter.native="runScan">
                </el-input>
              </el-form-item>
              <el-form-item v-if="personType === 'teacher'">
                <el-select
                  v-model="campusId"
                  clearable
                  filterable
                  placeholder="关联校区"
                  class="toolbar-field-w-md"
                  :disabled="busy">
                  <el-option
                    v-for="item in campusOptions"
                    :key="item.Id"
                    :label="item.Name"
                    :value="item.Id">
                  </el-option>
                </el-select>
              </el-form-item>
              <template v-if="isConvertMode">
                <el-form-item>
                  <el-tooltip
                    effect="dark"
                    placement="top"
                    content="检测非规范 .jpg 头像。透明 PNG 转 JPG 后透明区域变为白底；GIF 只转第一帧。">
                    <i class="el-icon-question hpc-help-icon"></i>
                  </el-tooltip>
                  <el-button
                    type="primary"
                    icon="el-icon-search"
                    :loading="scanning"
                    :disabled="!departmentId || busy"
                    @click="runScan">
                    检测格式
                  </el-button>
                </el-form-item>
                <el-form-item>
                  <el-select
                    v-model="listFilter"
                    class="toolbar-field-w-sm"
                    :disabled="busy || allRows.length === 0"
                    @change="onListFilterChange">
                    <el-option label="全部" value="all"></el-option>
                    <el-option label="需转换" value="convert"></el-option>
                    <el-option label="未知" value="unknown"></el-option>
                    <el-option label="探测失败" value="fail"></el-option>
                  </el-select>
                </el-form-item>
              </template>
              <template v-else>
                <el-form-item>
                  <el-input-number
                    v-model="thresholdKb"
                    :min="50"
                    :max="5120"
                    :step="50"
                    controls-position="right"
                    class="hpc-threshold"
                    :disabled="busy"
                    title="超过该 KB 数的头像将列入待压缩列表">
                  </el-input-number>
                  <span class="hpc-threshold-unit">KB 阈值</span>
                </el-form-item>
                <el-form-item>
                  <el-tooltip
                    effect="dark"
                    placement="top"
                    :content="'头像大于左侧阈值（当前 ' + (thresholdKb || 300) + 'KB）即标记为「超过阈值」，检测后默认只列出超过阈值与大小未知项；「压缩全部超过阈值」不含未知项，未知需手动勾选。'">
                    <i class="el-icon-question hpc-help-icon"></i>
                  </el-tooltip>
                  <el-button
                    type="primary"
                    icon="el-icon-search"
                    :loading="scanning"
                    :disabled="!departmentId || busy"
                    @click="runScan">
                    检测大小
                  </el-button>
                </el-form-item>
                <el-form-item>
                  <el-select
                    v-model="listFilter"
                    class="toolbar-field-w-sm"
                    :disabled="busy || allRows.length === 0"
                    @change="onListFilterChange">
                    <el-option label="全部" value="all"></el-option>
                    <el-option label="超过阈值" value="oversized"></el-option>
                    <el-option label="大小未知" value="unknown"></el-option>
                    <el-option label="探测失败" value="fail"></el-option>
                  </el-select>
                </el-form-item>
              </template>
            </div>
          </el-form>
        </div>

        <div class="hpc-hint" v-if="departmentName">
          <template v-if="personType === 'teacher'">
            当前学校：<strong>{{ departmentName }}</strong>
            <template v-if="selectedCampusLabel">
              <span class="hpc-hint-sep">·</span>
              校区：<strong>{{ selectedCampusLabel }}</strong>
            </template>
          </template>
          <template v-else>
            当前部门：<strong>{{ departmentName }}</strong>
            <span class="hpc-hint-sub">（含子部门；建议选年级或班级）</span>
          </template>
          <span class="hpc-hint-sub" v-if="isConvertMode"> · 非规范 .jpg 将列入列表；透明区域转 JPG 后为白底</span>
          <span class="hpc-hint-sub" v-else> · 超过 {{ thresholdKb || 300 }}KB 即列入列表</span>
        </div>
        <div class="hpc-hint hpc-hint--warn" v-else>
          {{ personType === 'teacher' ? '未获取到学校，请先在顶栏选择学校' : '请先在左侧选择部门' }}
        </div>

        <div v-if="scanSummary" class="hpc-summary-bar">
          {{ scanSummary }}
        </div>

        <div v-if="busy" class="hpc-progress">
          <div class="hpc-progress-row">
            <i class="el-icon-loading"></i>
            <span>{{ progressText }}</span>
            <span class="hpc-elapsed">已用时 {{ elapsedText }}</span>
          </div>
          <div v-if="scanning" class="hpc-indeterminate"></div>
          <el-progress
            v-else
            :percentage="executePercent"
            :stroke-width="10"
            style="margin-top: 8px;">
          </el-progress>
        </div>

        <div class="hpc-table-wrap" ref="compressTableWrap">
          <el-table
            ref="compressTable"
            :data="pagedRows"
            v-loading="false"
            :height="tableHeight"
            size="small"
            stripe
            highlight-current-row
            @row-click="onRowClick"
            @selection-change="onSelectionChange">
            <el-table-column type="selection" width="45" :selectable="rowSelectable"></el-table-column>
          <el-table-column label="头像" width="72">
            <template slot-scope="scope">
              <el-image
                v-if="scope.row.PreviewUrl"
                :src="resolvePreview(scope.row.PreviewUrl)"
                fit="cover"
                lazy
                class="hpc-avatar"
                :alt="(scope.row.Name || '头像') + ' 预览'"
                :preview-src-list="[resolvePreview(scope.row.PreviewUrl)]">
              </el-image>
              <span v-else class="hpc-no-photo">无</span>
            </template>
          </el-table-column>
          <el-table-column prop="Name" :label="personType === 'student' ? '姓名' : '教师'" min-width="90" show-overflow-tooltip></el-table-column>
          <el-table-column prop="DepartmentName" label="部门" min-width="100" show-overflow-tooltip></el-table-column>
          <el-table-column label="来源" width="80">
            <template slot-scope="scope">
              <el-tag size="mini" :type="storageTagType(scope.row.StorageType)">{{ storageLabel(scope.row.StorageType) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column v-if="isConvertMode" label="当前格式" width="100">
            <template slot-scope="scope">
              {{ formatLabel(scope.row.CurrentFormat) }}
            </template>
          </el-table-column>
          <el-table-column label="大小" width="100">
            <template slot-scope="scope">
              <span v-if="scope.row.ProbeStatus === 'ok' && hasSize(scope.row.MaxSizeKb)">{{ formatKb(scope.row.MaxSizeKb) }}</span>
              <span v-else-if="scope.row.ProbeStatus === 'ok'" class="hpc-muted">—</span>
              <el-tag v-else-if="scope.row.ProbeStatus === 'unknown'" type="info" size="mini">未知</el-tag>
              <el-tag v-else-if="scope.row.ProbeStatus === 'fail'" type="danger" size="mini">失败</el-tag>
              <span v-else class="hpc-muted">—</span>
            </template>
          </el-table-column>
          <el-table-column label="状态" width="96">
            <template slot-scope="scope">
              <template v-if="isConvertMode">
                <el-tag v-if="scope.row.ProbeStatus === 'fail'" type="danger" size="mini">失败</el-tag>
                <el-tag v-else-if="scope.row.ProbeStatus === 'unknown'" type="info" size="mini">未知</el-tag>
                <el-tag v-else-if="isRenameOnly(scope.row)" type="warning" size="mini">待改名</el-tag>
                <el-tag v-else-if="scope.row.NeedsConvert" type="warning" size="mini">需转换</el-tag>
                <el-tag v-else type="success" size="mini">已规范</el-tag>
              </template>
              <template v-else>
                <el-tag v-if="scope.row.IsOversized" type="warning" size="mini">超过阈值</el-tag>
                <el-tag v-else-if="scope.row.ProbeStatus === 'unknown'" type="info" size="mini">未知</el-tag>
                <el-tag v-else-if="scope.row.ProbeStatus === 'fail'" type="danger" size="mini">失败</el-tag>
                <el-tag v-else type="success" size="mini">正常</el-tag>
              </template>
            </template>
          </el-table-column>
          <el-table-column :label="isConvertMode ? '转换结果' : '压缩结果'" min-width="140" show-overflow-tooltip>
            <template slot-scope="scope">
              <span v-if="scope.row._compressStatus === 'success'">
                {{ formatKb(scope.row._beforeKb) }} → {{ formatKb(scope.row._afterKb) }}
              </span>
              <span v-else-if="scope.row._compressStatus === 'skip'" class="hpc-muted">{{ scope.row._compressMsg || '跳过' }}</span>
              <span v-else-if="scope.row._compressStatus === 'fail'" class="hpc-fail">{{ scope.row._compressMsg || '失败' }}</span>
              <span v-else class="hpc-muted">—</span>
            </template>
          </el-table-column>
        </el-table>
        </div>

        <el-pagination
          class="hpc-pagination"
          layout="total, prev, pager, next, jumper"
          :current-page.sync="page"
          :page-size="pageSize"
          :total="filteredRows.length"
          @current-change="onPageChange">
        </el-pagination>

        <div class="hpc-actions">
          <el-button
            type="primary"
            size="small"
            :loading="compressing"
            :disabled="!departmentId || selectedRows.length === 0 || busy"
            @click="executeCompress(false)">
            {{ isConvertMode ? '转换选中' : '压缩选中' }}（{{ selectedRows.length }}）
          </el-button>
          <el-button
            type="warning"
            size="small"
            :loading="compressing"
            :disabled="!departmentId || batchIds.length === 0 || busy"
            @click="executeCompress(true)">
            {{ isConvertMode ? '转换全部待处理' : '压缩全部超过阈值' }}（{{ batchIds.length }}）
          </el-button>
        </div>

        <el-alert
          v-if="executeSummary"
          :title="executeSummary"
          type="info"
          :closable="false"
          show-icon
          class="hpc-summary">
        </el-alert>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import DepartmentPane from './DepartmentPane'
import {
  scanStudentHeadPhotoCompress,
  executeStudentHeadPhotoCompress,
  scanTeacherHeadPhotoCompress,
  executeTeacherHeadPhotoCompress
} from '../api/modules/personnel'
import { getCampusList } from '../api/modules/misc'

const FRONTEND_BATCH_SIZE = 20

export default {
  name: 'HeadPhotoCompressDialog',
  components: { DepartmentPane },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    personType: {
      type: String,
      default: 'student'
    },
    /** 教师模式：顶栏学校部门 Id */
    schoolDepartmentId: {
      type: [String, Number],
      default: ''
    }
  },
  data() {
    return {
      activeMode: 'compress',
      departmentId: '',
      departmentName: '',
      nameKeyword: '',
      cardNoKeyword: '',
      thresholdKb: 300,
      allRows: [],
      selectedRows: [],
      scanning: false,
      compressing: false,
      progressText: '',
      executePercent: 0,
      elapsedSeconds: 0,
      elapsedTimer: null,
      scanElapsedSeconds: 0,
      compressElapsedSeconds: 0,
      scannedCount: 0,
      oversizedCount: 0,
      unknownCount: 0,
      failCount: 0,
      nonJpgCount: 0,
      executeSummary: '',
      page: 1,
      listFilter: 'all',
      campusId: null,
      campusOptions: [],
      tableHeight: 400
    }
  },
  computed: {
    pageSize() {
      return this.personType === 'teacher' ? 10 : 20
    },
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    },
    isConvertMode() {
      return this.activeMode === 'convertJpg'
    },
    dialogTitle() {
      return this.personType === 'student' ? '学生头像压缩' : '教师头像压缩'
    },
    busy() {
      return this.scanning || this.compressing
    },
    selectedCampusLabel() {
      if (!this.campusId) return ''
      const item = (this.campusOptions || []).find((c) => c.Id === this.campusId)
      return item ? item.Name : ''
    },
    filteredRows() {
      const rows = this.allRows || []
      if (this.listFilter === 'oversized') {
        return rows.filter((r) => r.IsOversized)
      }
      if (this.listFilter === 'convert') {
        return rows.filter((r) => r.NeedsConvert && r.ProbeStatus === 'ok')
      }
      if (this.listFilter === 'unknown') {
        return rows.filter((r) => r.ProbeStatus === 'unknown')
      }
      if (this.listFilter === 'fail') {
        return rows.filter((r) => r.ProbeStatus === 'fail')
      }
      return rows
    },
    pagedRows() {
      const start = (this.page - 1) * this.pageSize
      return this.filteredRows.slice(start, start + this.pageSize)
    },
    oversizedIds() {
      return this.allRows.filter((r) => r.IsOversized).map((r) => r.Id)
    },
    convertIds() {
      return this.allRows
        .filter((r) => r.NeedsConvert && r.ProbeStatus !== 'unknown' && r.ProbeStatus !== 'fail')
        .map((r) => r.Id)
    },
    batchIds() {
      return this.isConvertMode ? this.convertIds : this.oversizedIds
    },
    scanSummary() {
      if (!this.scannedCount && !this.allRows.length) return ''
      const parts = this.isConvertMode
        ? [
          `已扫描 ${this.scannedCount}`,
          `待处理 ${this.nonJpgCount}`,
          `未知 ${this.unknownCount}`,
          `失败 ${this.failCount}`,
          `列表 ${this.filteredRows.length}`
        ]
        : [
          `已扫描 ${this.scannedCount}`,
          `超过阈值 ${this.oversizedCount}`,
          `未知 ${this.unknownCount}`,
          `失败 ${this.failCount}`,
          `列表 ${this.filteredRows.length}`
        ]
      if (this.scanElapsedSeconds > 0) {
        parts.push(`扫描耗时 ${this.formatElapsed(this.scanElapsedSeconds)}`)
      }
      return parts.join(' · ')
    },
    elapsedText() {
      return this.formatElapsed(this.elapsedSeconds)
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.resetState()
        if (this.personType === 'teacher') {
          this.initTeacherSchoolScope()
        }
        this.$nextTick(() => this.syncTableHeight())
      } else {
        this.stopElapsedTimer()
        this.unbindTableResize()
      }
    },
    activeMode() {
      if (!this.visible) return
      this.clearScanResults()
      this.$nextTick(() => this.syncTableHeight())
    }
  },
  beforeDestroy() {
    this.stopElapsedTimer()
    this.unbindTableResize()
  },
  methods: {
    resetState() {
      this.executeSummary = ''
      this.scanning = false
      this.compressing = false
      this.progressText = ''
      this.executePercent = 0
      this.elapsedSeconds = 0
      this.scanElapsedSeconds = 0
      this.compressElapsedSeconds = 0
      this.scannedCount = 0
      this.oversizedCount = 0
      this.unknownCount = 0
      this.failCount = 0
      this.nonJpgCount = 0
      this.selectedRows = []
      this.allRows = []
      this.page = 1
      this.listFilter = 'all'
      this.nameKeyword = ''
      this.cardNoKeyword = ''
      this.thresholdKb = 300
      this.activeMode = 'compress'
      this.campusId = null
      this.campusOptions = []
      this.stopElapsedTimer()
      if (this.personType !== 'teacher') {
        this.departmentId = ''
        this.departmentName = ''
      }
    },
    initTeacherSchoolScope() {
      const id = this.schoolDepartmentId
        || this.$store.state.activeSchoolId
        || window.localStorage.getItem('activeSchoolId')
      if (!id) {
        this.departmentId = ''
        this.departmentName = ''
        return
      }
      this.departmentId = String(id)
      this.departmentName = this.$store.state.activeSchoolName
        || window.localStorage.getItem('activeSchoolName')
        || '当前学校'
      this.loadTeacherCampusOptions()
    },
    async loadTeacherCampusOptions() {
      if (!this.departmentId) {
        this.campusOptions = []
        this.campusId = null
        return
      }
      try {
        const res = await getCampusList({
          page: 1,
          intPageSize: 999,
          departmentId: this.departmentId
        })
        if (res.data.success) {
          this.campusOptions = res.data.response.data || []
        } else {
          this.campusOptions = []
        }
      } catch (e) {
        this.campusOptions = []
      }
    },
    handleBeforeClose(done) {
      if (this.busy) {
        this.$message.warning('操作进行中，请等待完成')
        return
      }
      done()
    },
    handleClose() {
      this.unbindTableResize()
      this.resetState()
    },
    /** 表格高度随可用区域自适应。最后更新：2026-09-15 */
    syncTableHeight() {
      const measure = () => {
        const wrap = this.$refs.compressTableWrap
        if (!wrap) return
        const h = Math.floor(wrap.clientHeight)
        if (h > 120 && h !== this.tableHeight) {
          this.tableHeight = h
          this.$nextTick(() => {
            if (this.$refs.compressTable && this.$refs.compressTable.doLayout) {
              this.$refs.compressTable.doLayout()
            }
          })
        }
      }
      this.$nextTick(() => {
        measure()
        setTimeout(measure, 60)
      })
      this.bindTableResize()
    },
    bindTableResize() {
      if (this._hpcResizeBound) return
      this._hpcOnResize = () => this.syncTableHeight()
      window.addEventListener('resize', this._hpcOnResize)
      this._hpcResizeBound = true
    },
    unbindTableResize() {
      if (this._hpcOnResize) {
        window.removeEventListener('resize', this._hpcOnResize)
        this._hpcOnResize = null
      }
      this._hpcResizeBound = false
    },
    onTreeNodeClick(data) {
      if (this.busy) return
      this.departmentId = data.value
      this.departmentName = data.label || data.name || ''
      this.page = 1
      this.allRows = []
      this.scannedCount = 0
      this.oversizedCount = 0
      this.unknownCount = 0
      this.failCount = 0
      this.nonJpgCount = 0
      this.listFilter = 'all'
      this.selectedRows = []
      this.executeSummary = ''
      this.$nextTick(() => this.syncTableHeight())
    },
    onPageChange() {
      this.$nextTick(() => this.restoreSelectionOnPage())
    },
    onListFilterChange() {
      this.page = 1
      // 探测失败不可压缩，筛选切换时清掉失败勾选
      this.selectedRows = this.selectedRows.filter((r) => this.rowSelectable(r))
      this.$nextTick(() => this.restoreSelectionOnPage())
    },
    /** 点击行切换勾选（跳过复选框与头像预览）。最后更新：2026-09-15 */
    onRowClick(row, column, event) {
      if (this.busy || !row || !this.rowSelectable(row)) return
      if (event && event.target) {
        const target = event.target
        if (
          target.classList.contains('el-checkbox__original') ||
          target.classList.contains('el-checkbox__inner') ||
          (target.closest && (target.closest('.el-checkbox') || target.closest('.el-image') || target.closest('.el-image-viewer__wrapper')))
        ) {
          return
        }
      }
      const table = this.$refs.compressTable
      if (!table) return
      table.toggleRowSelection(row)
    },
    onSelectionChange(val) {
      const pageIds = new Set(this.pagedRows.map((r) => r.Id))
      const kept = this.selectedRows.filter((r) => !pageIds.has(r.Id))
      const next = (val || []).filter((r) => this.rowSelectable(r))
      this.selectedRows = kept.concat(next)
    },
    restoreSelectionOnPage() {
      const table = this.$refs.compressTable
      if (!table) return
      const selectedIds = new Set(this.selectedRows.map((r) => r.Id))
      table.clearSelection()
      this.pagedRows.forEach((row) => {
        if (selectedIds.has(row.Id) && this.rowSelectable(row)) {
          table.toggleRowSelection(row, true)
        }
      })
    },
    rowSelectable(row) {
      if (this.busy) return false
      // 探测失败无法压缩，禁止勾选
      return row && row.ProbeStatus !== 'fail'
    },
    resolvePreview(url) {
      if (!url) return ''
      if (typeof this.$resolveImageSrc === 'function') {
        return this.$resolveImageSrc(url)
      }
      return url
    },
    storageLabel(type) {
      const map = { local: '本机', oss: 'OSS', https: 'HTTPS', mixed: '混合' }
      return map[type] || type || '-'
    },
    storageTagType(type) {
      if (type === 'oss') return 'success'
      if (type === 'https') return 'warning'
      if (type === 'mixed') return 'info'
      return ''
    },
    formatKb(kb) {
      const n = Number(kb)
      if (!n || n <= 0) return '0KB'
      return `${n.toFixed(1)}KB`
    },
    hasSize(kb) {
      const n = Number(kb)
      return Number.isFinite(n) && n > 0
    },
    formatLabel(format) {
      if (!format) return '—'
      const map = { jpeg: 'JPG', png: 'PNG', bmp: 'BMP', gif: 'GIF', webp: 'WebP', tiff: 'TIFF', unknown: '未知' }
      return String(format)
        .split(',')
        .map((f) => map[f.trim().toLowerCase()] || f.trim().toUpperCase())
        .join(',')
    },
    isRenameOnly(row) {
      if (!row || !row.NeedsConvert || row.ProbeStatus !== 'ok') return false
      const fmt = String(row.CurrentFormat || '').toLowerCase()
      return fmt === 'jpeg' || fmt.split(',').every((f) => f.trim() === 'jpeg')
    },
    beforeTabLeave() {
      if (this.busy) {
        this.$message.warning('操作进行中，请等待完成')
        return false
      }
      return true
    },
    clearScanResults() {
      this.allRows = []
      this.selectedRows = []
      this.page = 1
      this.listFilter = 'all'
      this.scannedCount = 0
      this.oversizedCount = 0
      this.unknownCount = 0
      this.failCount = 0
      this.nonJpgCount = 0
      this.executeSummary = ''
      this.scanElapsedSeconds = 0
      this.compressElapsedSeconds = 0
    },
    formatElapsed(sec) {
      const s = Math.max(0, Number(sec) || 0)
      const m = Math.floor(s / 60)
      const r = s % 60
      return `${m}:${String(r).padStart(2, '0')}`
    },
    startElapsedTimer() {
      this.stopElapsedTimer()
      this.elapsedSeconds = 0
      this.elapsedTimer = setInterval(() => {
        this.elapsedSeconds += 1
      }, 1000)
    },
    stopElapsedTimer() {
      if (this.elapsedTimer) {
        clearInterval(this.elapsedTimer)
        this.elapsedTimer = null
      }
    },
    chunkIds(ids, size) {
      const list = []
      for (let i = 0; i < ids.length; i += size) {
        list.push(ids.slice(i, i + size))
      }
      return list
    },
    async runScan() {
      if (!this.departmentId) {
        this.$message.warning(this.personType === 'teacher' ? '请先在顶栏选择学校' : '请先在左侧选择部门')
        return
      }
      this.scanning = true
      this.executeSummary = ''
      this.progressText = this.isConvertMode ? '正在检测头像格式…' : '正在检测头像大小…'
      this.startElapsedTimer()
      try {
        const payload = {
          DepartmentId: String(this.departmentId),
          Mode: this.isConvertMode ? 'convertJpg' : 'compress'
        }
        if (this.isConvertMode) {
          payload.NonJpgOnly = true
        } else {
          payload.ThresholdKb = this.thresholdKb || 300
          payload.OversizedOnly = true
        }
        if (this.personType === 'student') {
          payload.StuName = (this.nameKeyword || '').trim()
          payload.CardNo = (this.cardNoKeyword || '').trim()
        } else {
          payload.TeacherName = (this.nameKeyword || '').trim()
          if (this.campusId) {
            payload.CampusId = this.campusId
          }
        }
        const request = this.personType === 'student'
          ? scanStudentHeadPhotoCompress
          : scanTeacherHeadPhotoCompress
        const res = await request(payload)
        if (!res.data.success) {
          this.$message.error(res.data.msg || '扫描失败')
          return
        }
        const data = res.data.response || {}
        this.scannedCount = data.ScannedCount || 0
        this.oversizedCount = data.OversizedCount || 0
        this.unknownCount = data.UnknownCount || 0
        this.nonJpgCount = data.NonJpgCount || 0
        this.allRows = (data.Items || []).map((item) => ({
          ...item,
          _compressStatus: '',
          _compressMsg: '',
          _beforeKb: 0,
          _afterKb: 0
        }))
        this.failCount = typeof data.FailCount === 'number'
          ? data.FailCount
          : this.allRows.filter((r) => r.ProbeStatus === 'fail').length
        this.page = 1
        this.listFilter = this.isConvertMode
          ? (this.nonJpgCount > 0 ? 'convert' : 'all')
          : (this.oversizedCount > 0 ? 'oversized' : 'all')
        this.selectedRows = []
        this.scanElapsedSeconds = this.elapsedSeconds
        if (this.isConvertMode) {
          this.$message.success(`扫描完成：待处理 ${this.nonJpgCount}，未知 ${this.unknownCount}，失败 ${this.failCount}`)
        } else {
          this.$message.success(`扫描完成：超过阈值 ${this.oversizedCount}，未知 ${this.unknownCount}，失败 ${this.failCount}`)
        }
        this.$nextTick(() => {
          this.syncTableHeight()
          this.autoSelectBatchOnPage()
        })
      } catch (e) {
        this.$message.error('扫描请求失败，请稍后重试')
      } finally {
        this.stopElapsedTimer()
        this.scanning = false
        this.progressText = ''
      }
    },
    autoSelectBatchOnPage() {
      const table = this.$refs.compressTable
      if (!table) return
      table.clearSelection()
      const selected = []
      this.pagedRows.forEach((row) => {
        const match = this.isConvertMode
          ? (row.NeedsConvert && this.rowSelectable(row) && row.ProbeStatus === 'ok')
          : (row.IsOversized && this.rowSelectable(row))
        if (match) {
          table.toggleRowSelection(row, true)
          selected.push(row)
        }
      })
      this.selectedRows = selected
    },
    async executeCompress(allBatch) {
      if (!this.departmentId) {
        this.$message.warning(this.personType === 'teacher' ? '请先在顶栏选择学校' : '请先在左侧选择部门')
        return
      }
      let ids = []
      if (allBatch) {
        ids = this.batchIds.slice()
      } else {
        ids = this.selectedRows.filter((r) => this.rowSelectable(r)).map((r) => r.Id)
      }
      if (!ids.length) {
        this.$message.warning(this.isConvertMode
          ? (allBatch ? '没有待转换的头像' : '请勾选待转换人员（探测失败不可选）')
          : (allBatch ? '没有超过阈值的头像可压缩' : '请勾选待压缩人员（探测失败不可选）'))
        return
      }

      const verb = this.isConvertMode ? '转换' : '压缩'
      const tip = allBatch
        ? (this.isConvertMode
          ? `确认转换全部待处理头像（${ids.length} 人）？将按每批 ${FRONTEND_BATCH_SIZE} 人处理。`
          : `确认压缩全部超过阈值的头像（${ids.length} 人）？将按每批 ${FRONTEND_BATCH_SIZE} 人处理。`)
        : `确认${verb}选中的 ${ids.length} 人？`
      try {
        await this.$confirm(tip, `确认${verb}`, { type: 'warning' })
      } catch (e) {
        return
      }

      this.compressing = true
      this.executeSummary = ''
      this.executePercent = 0
      this.progressText = `正在${verb} 0/${ids.length}…`
      this.startElapsedTimer()

      const chunks = this.chunkIds(ids, FRONTEND_BATCH_SIZE)
      let successCount = 0
      let skipCount = 0
      let failCount = 0
      let done = 0
      const rowMap = {}
      this.allRows.forEach((r) => {
        rowMap[r.Id] = r
      })

      try {
        const request = this.personType === 'student'
          ? executeStudentHeadPhotoCompress
          : executeTeacherHeadPhotoCompress

        for (let i = 0; i < chunks.length; i++) {
          const chunk = chunks[i]
          this.progressText = `正在${verb} ${done}/${ids.length}…（第 ${i + 1}/${chunks.length} 批）`
          const res = await request({
            DepartmentId: String(this.departmentId),
            Ids: chunk,
            Mode: this.isConvertMode ? 'convertJpg' : 'compress'
          })
          if (!res.data.success) {
            failCount += chunk.length
            chunk.forEach((id) => {
              const row = rowMap[id]
              if (row) {
                row._compressStatus = 'fail'
                row._compressMsg = res.data.msg || `${verb}失败`
              }
            })
          } else {
            const result = res.data.response || {}
            successCount += result.SuccessCount || 0
            skipCount += result.SkipCount || 0
            failCount += result.FailCount || 0
            ;(result.Items || []).forEach((item) => {
              const row = rowMap[item.Id]
              if (!row) return
              row._compressStatus = item.Status || (item.Success ? 'success' : 'fail')
              row._compressMsg = item.Message || ''
              row._beforeKb = item.BeforeKb || 0
              row._afterKb = item.AfterKb || 0
              if (item.NewHeadPhotoImage) {
                row.HeadPhotoImage = item.NewHeadPhotoImage
              }
            })
          }
          done += chunk.length
          this.executePercent = Math.min(100, Math.round((done / ids.length) * 100))
          this.progressText = `正在${verb} ${done}/${ids.length}…`
        }

        this.compressElapsedSeconds = this.elapsedSeconds
        this.executeSummary = `完成：成功 ${successCount}，跳过 ${skipCount}，失败 ${failCount}；${verb}耗时 ${this.formatElapsed(this.compressElapsedSeconds)}`
        if (failCount > 0) {
          this.$message.warning(this.executeSummary)
        } else {
          this.$message.success(this.executeSummary)
        }
        this.$emit('done')
      } catch (e) {
        this.$message.error(`${verb}请求失败，请稍后重试`)
      } finally {
        this.stopElapsedTimer()
        this.compressing = false
        this.progressText = ''
        this.executePercent = 100
      }
    }
  }
}
</script>

<style scoped>
.hpc-layout {
  display: flex;
  gap: 16px;
  height: 640px;
  max-height: calc(90vh - 140px);
  min-height: 420px;
}
.hpc-layout--teacher {
  height: 640px;
  max-height: calc(90vh - 140px);
  min-height: 420px;
}
.hpc-layout--teacher .hpc-right {
  width: 100%;
}
.hpc-left {
  width: 320px;
  flex-shrink: 0;
  border-right: 1px solid #ebeef5;
  padding-right: 8px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}
.hpc-left >>> .department-pane {
  flex: 1;
  min-height: 0;
  height: 100% !important;
  overflow: hidden !important;
}
.hpc-left >>> .pane-content-wrapper {
  flex: 1;
  min-height: 0;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.hpc-left >>> .department-pane .el-tree,
.hpc-left >>> .department-pane .flow-tree {
  flex: 1 1 0 !important;
  min-height: 0 !important;
  height: 0 !important;
  overflow-x: hidden !important;
  overflow-y: auto !important;
}
.hpc-left >>> .toggle-tree-bar,
.hpc-left >>> .pane-collapse-btn,
.hpc-left >>> .pane-expand-btn,
.hpc-left >>> .pane-expand-tab {
  display: none;
}
.hpc-right {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.hpc-right > .toolbar-filter-bar {
  flex-shrink: 0;
}
.hpc-tabs {
  margin: -4px 0 8px;
  flex-shrink: 0;
}
.hpc-tabs >>> .el-tabs__header {
  margin: 0;
}
.hpc-tabs >>> .el-tabs__content {
  display: none;
}
.hpc-tabs >>> .el-tabs__item {
  height: 36px;
  line-height: 36px;
  font-weight: 500;
  transition: color 0.2s ease;
}
.hpc-tabs >>> .el-tabs__item:focus {
  outline: none;
  box-shadow: none;
}
.hpc-threshold {
  width: 120px;
}
.hpc-threshold-unit {
  color: #64748b;
  font-size: 12px;
  margin-left: 4px;
}
.hpc-help-icon {
  color: #94a3b8;
  cursor: help;
  font-size: 14px;
  margin-right: 6px;
  transition: color 0.2s ease;
}
.hpc-help-icon:hover {
  color: var(--btn-primary, #2563eb);
}
.hpc-hint-sep {
  margin: 0 4px;
  color: #cbd5e1;
}
.hpc-hint {
  font-size: 13px;
  color: #475569;
  margin-bottom: 8px;
  line-height: 1.5;
  flex-shrink: 0;
}
.hpc-hint-sub {
  color: #64748b;
  margin-left: 4px;
}
.hpc-hint--warn {
  color: #d97706;
}
.hpc-summary-bar {
  font-size: 13px;
  color: #0f172a;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 7px 12px;
  margin-bottom: 8px;
  flex-shrink: 0;
}
.hpc-table-wrap {
  flex: 1 1 0;
  min-height: 0;
  height: 0;
  overflow: hidden;
}
.hpc-table-wrap >>> .el-table__row {
  cursor: pointer;
  transition: background-color 0.15s ease;
}
.hpc-avatar {
  width: 48px;
  height: 48px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: opacity 0.2s ease;
}
.hpc-avatar:hover {
  opacity: 0.88;
}
.hpc-no-photo {
  color: #94a3b8;
  font-size: 12px;
}
.hpc-pagination {
  margin-top: 8px;
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  flex-shrink: 0;
}
.hpc-actions {
  margin-top: 12px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  flex-shrink: 0;
}
.hpc-progress {
  margin-bottom: 10px;
  padding: 10px 12px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
}
.hpc-progress-row {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #1d4ed8;
  font-size: 13px;
}
.hpc-elapsed {
  margin-left: auto;
  color: #475569;
  font-variant-numeric: tabular-nums;
}
.hpc-indeterminate {
  margin-top: 8px;
  height: 8px;
  border-radius: 4px;
  background: linear-gradient(90deg, #dbeafe 25%, #3b82f6 50%, #dbeafe 75%);
  background-size: 200% 100%;
  animation: hpc-stripe 1.2s linear infinite;
}
@keyframes hpc-stripe {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}
@media (prefers-reduced-motion: reduce) {
  .hpc-indeterminate {
    animation: none;
    background: #93c5fd;
  }
  .hpc-avatar,
  .hpc-help-icon,
  .toolbar-filter-bar >>> .el-button,
  .hpc-table-wrap >>> .el-table__row {
    transition: none;
  }
}
.hpc-summary {
  margin-top: 10px;
}
.hpc-muted {
  color: #64748b;
}
.hpc-fail {
  color: #dc2626;
}
</style>

<style>
/* 头像压缩：自管高度，不受全局 body 限高影响。最后修改：2026-09-14 */
.hpc-dialog .el-dialog__body {
  max-height: none !important;
  padding: 12px 20px 16px !important;
  overflow: hidden;
}
.hpc-dialog .el-dialog__header {
  padding: 14px 20px 10px;
  border-bottom: 1px solid #e2e8f0;
}
.hpc-dialog .el-dialog__title {
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
}
</style>
